import { StateCreator } from 'zustand';

// Actions
import { getOptionData } from '@/actions/client';

// Types
import {
  OptionDataSlice,
  OptionDataState,
  OptionDataObject,
  IndividualOptionDataType,
  getIndividualTypesFromGrouped,
  getGroupedTypeFromIndividual,
} from './types';
import { OptionResponse } from '@shared/types';

// Constants
import { MasterCategoryType, MESSAGES } from '@shared/constants';
import { DEFAULT_OPTION_DATA_CACHE_TIME } from '@/constants/config';
import { isCacheExpired, showErrorToast } from '@/utils';

export type { OptionDataSlice } from './types';

// Initial states
const initialOptionDataState: OptionDataState = {
  optionData: {
    certificationNames: [],
    certificationOrganizations: [],
    degrees: [],
    fieldsOfStudy: [],
    jobTitles: [],
    schools: [],
    socialLinks: [],
    technicalSkills: [],
    technicalSkillCategories: [],
  },
  isOptionDataLoading: false,
  isOptionDataFetched: false,
  optionDataLoadingStates: {
    jobTitles: false,
    technicalSkills: false,
    technicalSkillCategories: false,
    socialLinks: false,
    degrees: false,
    schools: false,
    fieldsOfStudy: false,
    certificationNames: false,
    certificationOrganizations: false,
  },
  optionDataLastFetched: {
    jobTitles: 0,
    technicalSkills: 0,
    technicalSkillCategories: 0,
    socialLinks: 0,
    degrees: 0,
    schools: 0,
    fieldsOfStudy: 0,
    certificationNames: 0,
    certificationOrganizations: 0,
  },
  optionDataErrors: {
    jobTitles: null,
    technicalSkills: null,
    technicalSkillCategories: null,
    socialLinks: null,
    degrees: null,
    schools: null,
    fieldsOfStudy: null,
    certificationNames: null,
    certificationOrganizations: null,
  },
};

// Helper function to fetch data from API
const fetchOptionDataFromAPI = async (
  types: MasterCategoryType[],
  token?: string,
) => {
  // Remove duplicates from types array
  const masterCategoryTypes = types.filter(
    (type, index) => types.indexOf(type) === index,
  );

  const { data, error } = await getOptionData(masterCategoryTypes, { token });

  if (error || !data) {
    showErrorToast(error);
  }

  // Map grouped types to individual types and filter data
  const individualTypes: IndividualOptionDataType[] = [];
  types.forEach((type) => {
    individualTypes.push(...getIndividualTypesFromGrouped(type));
  });

  // Filter data by requested individual types
  const filteredData: Partial<
    Record<IndividualOptionDataType, OptionResponse[]>
  > = {};
  individualTypes.forEach((type) => {
    if (data && data[type]) {
      filteredData[type] = data[type];
    }
  });

  return { filteredData, fullData: data };
};

export const createOptionDataSlice: StateCreator<
  OptionDataSlice,
  [],
  [],
  OptionDataSlice
> = (set, get) => ({
  ...initialOptionDataState,

  // Basic setters
  setOptionData: (data: OptionDataObject | null) => set({ optionData: data }),
  setOptionDataLoading: (loading: boolean) =>
    set({ isOptionDataLoading: loading }),
  setOptionDataFetched: (fetched: boolean) =>
    set({ isOptionDataFetched: fetched }),

  // Fetch specific types with advanced caching
  fetchOptionDataByTypes: async (
    types: MasterCategoryType[],
    force = false,
    token?: string,
  ) => {
    const state = get();

    // Convert grouped types to individual types for checking
    const individualTypesToCheck: IndividualOptionDataType[] = [];
    types.forEach((type) => {
      individualTypesToCheck.push(...getIndividualTypesFromGrouped(type));
    });

    // Filter individual types that need fetching
    const individualTypesToFetch = individualTypesToCheck.filter(
      (individualType) => {
        const shouldRefetch =
          state.shouldRefetchOptionData(
            getGroupedTypeFromIndividual(individualType),
          ) || force;
        const isNotLoading = !state.optionDataLoadingStates[individualType];
        return shouldRefetch && isNotLoading;
      },
    );

    if (individualTypesToFetch.length === 0) {
      return;
    }

    // Set loading states for individual types
    set((currentState) => ({
      optionDataLoadingStates: {
        ...currentState.optionDataLoadingStates,
        ...individualTypesToFetch.reduce(
          (acc, type) => ({ ...acc, [type]: true }),
          {},
        ),
      },
      optionDataErrors: {
        ...currentState.optionDataErrors,
        ...individualTypesToFetch.reduce(
          (acc, type) => ({ ...acc, [type]: null }),
          {},
        ),
      },
    }));

    try {
      state.setOptionDataLoading(true);
      // Fetch data using original grouped types to avoid API issues
      const originalTypes = individualTypesToFetch
        .map(getGroupedTypeFromIndividual)
        .filter((type, index, array) => array.indexOf(type) === index);
      const { filteredData } = await fetchOptionDataFromAPI(
        originalTypes,
        token,
      );

      // Update state with successful results
      set((currentState) => {
        const newLoadingStates = { ...currentState.optionDataLoadingStates };
        const newLastFetched = { ...currentState.optionDataLastFetched };
        const newOptionData =
          currentState.optionData || ({} as OptionDataObject);

        individualTypesToFetch.forEach((individualType) => {
          // Always set loading to false after fetch attempt
          newLoadingStates[individualType] = false;
          newLastFetched[individualType] = Date.now();
          state.setOptionDataLoading(false);

          if (filteredData[individualType]) {
            // Update the option data object with individual type data
            newOptionData[individualType] = filteredData[individualType]!;
          }
        });

        return {
          optionData: newOptionData,
          optionDataLoadingStates: newLoadingStates,
          optionDataLastFetched: newLastFetched,
          isOptionDataFetched: true,
        };
      });
    } catch (error) {
      // Update state with error
      set((currentState) => {
        const newLoadingStates = { ...currentState.optionDataLoadingStates };
        const newErrors = { ...currentState.optionDataErrors };

        individualTypesToFetch.forEach((individualType) => {
          newLoadingStates[individualType] = false;
          newErrors[individualType] =
            error instanceof Error ? error.message : String(error);
        });

        return {
          optionDataLoadingStates: newLoadingStates,
          optionDataErrors: newErrors,
        };
      });

      const errorMessage =
        error instanceof Error
          ? error.message
          : MESSAGES.FETCH_OPTIONS_DATA_ERROR;
      showErrorToast(errorMessage);
    }
  },

  // Get data by type (handles grouped types)
  getOptionDataByType: (type: MasterCategoryType) => {
    const state = get();
    const individualTypes = getIndividualTypesFromGrouped(type);

    // If it's a single type, return directly
    if (individualTypes.length === 1) {
      const individualType = individualTypes[0];
      if (!individualType || !state.optionData) return [];

      return {
        [individualType]: state.optionData[individualType] || [],
      };
    }

    // For grouped types, return data structured by individual types
    const optionData: Partial<
      Record<IndividualOptionDataType, OptionResponse[]>
    > = {};
    individualTypes.forEach((individualType) => {
      optionData[individualType] = state.optionData?.[individualType] || [];
    });

    return optionData as Partial<
      Record<IndividualOptionDataType, OptionResponse[]>
    >;
  },

  // Check if should refetch (handles grouped types)
  shouldRefetchOptionData: (
    type: MasterCategoryType,
    cacheTimeMs = DEFAULT_OPTION_DATA_CACHE_TIME,
  ) => {
    const state = get();
    const individualTypes = getIndividualTypesFromGrouped(type);

    // Check if any individual type needs refetching
    return individualTypes.some((individualType) => {
      const lastFetched = state.optionDataLastFetched[individualType];
      const hasData = (state.optionData?.[individualType]?.length ?? 0) > 0;
      const isExpired = isCacheExpired(lastFetched, cacheTimeMs);

      return !hasData || isExpired;
    });
  },

  // Clear data by type (handles grouped types)
  clearOptionDataByType: (type?: MasterCategoryType) => {
    if (type) {
      const individualTypes = getIndividualTypesFromGrouped(type);
      set((state) => {
        const newOptionData = { ...state.optionData } as OptionDataObject;
        const newLastFetched = { ...state.optionDataLastFetched };
        const newErrors = { ...state.optionDataErrors };

        individualTypes.forEach((individualType) => {
          newOptionData[individualType] = [];
          newLastFetched[individualType] = 0;
          newErrors[individualType] = null;
        });

        return {
          optionData: newOptionData,
          optionDataLastFetched: newLastFetched,
          optionDataErrors: newErrors,
        };
      });
    } else {
      // Reset to initial state
      set(initialOptionDataState);
    }
  },

  // Reset all option data
  resetOptionData: () => {
    set(initialOptionDataState);
  },
});
